<div class="max-w-full text-base-content">
  <!-- Bank Transfer Instructions -->
  <div class="bg-info/10 border border-info/20 rounded-lg p-4 mb-4">
    <div class="flex items-start space-x-2 mb-3">
      <i class="fa-solid fa-circle-info text-info text-lg mt-0.5"></i>
      <div>
        <h5 class="font-semibold text-base-content m-0 mb-2">
          How to Pay via Bank Transfer
        </h5>
        <ol class="text-sm text-base-content/80 space-y-1.5 pl-5 m-0 list-decimal">
          <li>Transfer the amount due to the bank account below</li>
          <li>Keep your payment receipt/screenshot</li>
          <li>Upload the receipt to complete your payment</li>
        </ol>
      </div>
    </div>
  </div>

  @if (amount !== undefined && amount !== null) {
    <div class="bg-primary/10 border-2 border-primary/30 rounded-xl p-4 mb-4 flex items-center justify-between flex-wrap gap-3">
      <div>
        <span class="text-xs font-semibold text-base-content/70 uppercase tracking-wide block">Amount to Transfer</span>
        <span class="text-2xl font-black text-primary tracking-tight">
          {{ currency }} {{ amount | number: '1.2-2' }}
        </span>
      </div>
      @if (referenceNumber) {
        <div class="text-left sm:text-right">
          <span class="text-xs font-semibold text-base-content/70 uppercase tracking-wide block">Payment Reference</span>
          <span class="text-sm font-mono font-bold text-base-content bg-base-100 px-2.5 py-1 rounded border border-base-300 inline-block shadow-sm">
            {{ referenceNumber }}
          </span>
        </div>
      }
    </div>
  }

  <form [formGroup]="paymentOptionForm">
    <!-- Bank Account Details -->
    <div class="bg-base-200/50 border border-base-300 rounded-lg p-4 mb-4">
      <div class="flex items-center justify-between mb-3">
        <h5 class="font-semibold text-base-content m-0">Transfer Payment To:</h5>
        @if (selectedBankAccount) {
          <span class="daisy-badge daisy-badge-primary daisy-badge-sm">{{ selectedBankAccount.bankName }}</span>
        } @else {
          <span class="daisy-badge daisy-badge-primary daisy-badge-sm">Bank Transfer</span>
        }
      </div>

      <!-- If multiple bank accounts, allow selecting -->
      @if (bankAccounts && bankAccounts.length > 1) {
        <div class="mb-3">
          <label class="daisy-label p-0 mb-1">
            <span class="daisy-label-text text-xs font-medium">Select Bank Account:</span>
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            @for (acc of bankAccounts; track acc.accountNumber) {
              <button
                type="button"
                class="p-2.5 rounded-lg border text-left transition-all flex items-center justify-between"
                [class.border-primary]="selectedBankAccount?.accountNumber === acc.accountNumber"
                [class.bg-primary/10]="selectedBankAccount?.accountNumber === acc.accountNumber"
                [class.border-base-300]="selectedBankAccount?.accountNumber !== acc.accountNumber"
                [class.bg-base-100]="selectedBankAccount?.accountNumber !== acc.accountNumber"
                (click)="onSelectBankAccount(acc)"
              >
                <div>
                  <p class="font-medium text-xs text-base-content m-0">{{ acc.bankName }}</p>
                  <p class="text-xs text-base-content/60 font-mono m-0">{{ acc.accountNumber }}</p>
                </div>
                @if (selectedBankAccount?.accountNumber === acc.accountNumber) {
                  <i class="fa-solid fa-circle-check text-primary text-sm"></i>
                }
              </button>
            }
          </div>
        </div>
      }

      @if (selectedBankAccount) {
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div class="bg-base-100 border border-base-200 p-3 rounded">
            <p class="text-base-content/60 text-xs mb-1 font-medium">Bank Name</p>
            <p class="font-semibold text-base-content m-0">{{ selectedBankAccount.bankName }}</p>
          </div>
          <div class="bg-base-100 border border-base-200 p-3 rounded">
            <p class="text-base-content/60 text-xs mb-1 font-medium">Branch</p>
            <p class="font-semibold text-base-content m-0">{{ selectedBankAccount.branchName || 'Main' }}</p>
          </div>
          <div class="bg-primary/10 border border-primary/20 p-3 rounded col-span-2">
            <p class="text-base-content/60 text-xs mb-1 font-medium">Account Number</p>
            <p class="font-bold text-primary text-lg m-0 tracking-wide font-mono">
              {{ selectedBankAccount.accountNumber }}
            </p>
          </div>
          <div class="bg-base-100 border border-base-200 p-3 rounded col-span-2">
            <p class="text-base-content/60 text-xs mb-1 font-medium">Account Holder</p>
            <p class="font-semibold text-base-content m-0">
              {{ selectedBankAccount.accountName }}
            </p>
          </div>
        </div>
      } @else {
        <div class="daisy-alert daisy-alert-error text-xs">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>Bank transfer is currently unavailable. Please contact the academy administrator.</span>
        </div>
      }
    </div>

    <input type="hidden" formControlName="bank" />

    <!-- Upload Payment Receipt -->
    <div class="bg-warning/10 border-2 border-warning/40 rounded-lg p-4 mb-4">
      <div class="flex items-center mb-3">
        <i class="fa-solid fa-file-arrow-up text-warning text-xl mr-2"></i>
        <h5 class="font-semibold text-base-content m-0">
          Upload Payment Receipt <span class="text-error">*</span>
        </h5>
      </div>

      <div class="bg-base-100 border border-base-200 rounded p-3 mb-3">
        <p class="text-sm text-base-content font-medium mb-2 m-0">
          Acceptable documents:
        </p>
        <ul class="text-sm text-base-content/70 space-y-1 pl-5 m-0 list-disc">
          <li>Bank transfer receipt screenshot</li>
          <li>Mobile banking app confirmation</li>
          <li>ATM receipt photo or scan</li>
        </ul>
      </div>

      <file-pond
        #myPond
        [options]="pondOptions"
        (oninit)="pondHandleInit()"
        (onaddfile)="pondHandleAddFile($event)"
        (onprocessfile)="pondHandleProcessFile($event)"
      >
      </file-pond>

      <p class="text-xs text-base-content/60 mt-2 m-0 flex items-center">
        <i class="fa-solid fa-info-circle mr-1"></i>
        Formats: PNG, JPG, PDF, DOC, DOCX, XLS, XLSX
      </p>

      @if (
        paymentOptionForm.invalid &&
        (paymentOptionForm.controls.slip.dirty ||
          paymentOptionForm.controls.slip.touched)
      ) {
        <div
          class="bg-error/10 border border-error/20 rounded p-3 mt-3 text-sm text-error"
        >
          @if (paymentOptionForm.controls.slip.hasError('required')) {
            <div class="flex items-center">
              <i class="fa-solid fa-triangle-exclamation mr-2"></i>
              <span>Payment receipt is required to complete your payment</span>
            </div>
          }
        </div>
      }
    </div>
  </form>

  <!-- Submit Button -->
  <div class="mt-6">
    <button
      class="daisy-btn daisy-btn-primary daisy-btn-block daisy-btn-lg"
      [disabled]="!selectedBankAccount"
      (click)="processPayment()"
      type="button"
    >
      <i class="fa-solid fa-circle-check mr-2"></i>
      Complete Payment Submission
    </button>
    <p class="text-xs text-base-content/60 text-center mt-2 m-0">
      Your payment will be verified within 24 hours
    </p>
  </div>
</div>
